import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getTestimonials, createTestimonial, updateTestimonial, deleteTestimonial } from '../services/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [formData, setFormData] = useState({
    name: '', location_en: '', location_zh: '',
    rating: 5, text_en: '', text_zh: '', active: true
  });

  const fetchTestimonials = async () => {
    try {
      const res = await getTestimonials();
      setTestimonials(res.data);
    } catch (error) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const handleEdit = (testimonial) => {
    setEditingTestimonial(testimonial);
    setFormData(testimonial);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingTestimonial(null);
    setFormData({
      name: '', location_en: '', location_zh: '',
      rating: 5, text_en: '', text_zh: '', active: true
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingTestimonial) {
        await updateTestimonial(editingTestimonial.id, formData);
        toast.success('Testimonial updated');
      } else {
        await createTestimonial(formData);
        toast.success('Testimonial created');
      }
      fetchTestimonials();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await deleteTestimonial(id);
      setTestimonials(testimonials.filter(t => t.id !== id));
      toast.success('Testimonial deleted');
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const handleToggleActive = async (testimonial) => {
    try {
      await updateTestimonial(testimonial.id, { active: !testimonial.active });
      setTestimonials(testimonials.map(t => t.id === testimonial.id ? { ...t, active: !t.active } : t));
    } catch (error) {
      toast.error('Failed to update testimonial');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Testimonials</h1>
          <p className="text-slate-600">Manage customer reviews and testimonials</p>
        </div>
        <Button onClick={handleCreate} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
          <Plus className="w-4 h-4 mr-2" /> Add Testimonial
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {testimonials.map((testimonial) => (
          <Card key={testimonial.id} className={`p-6 bg-white border-2 border-slate-300 ${!testimonial.active ? 'opacity-50' : ''}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-slate-600">{testimonial.name?.charAt(0)}</span>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{testimonial.name}</h3>
                  <p className="text-sm text-slate-500">{testimonial.location_en} | {testimonial.location_zh}</p>
                </div>
              </div>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${i < testimonial.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
                  />
                ))}
              </div>
            </div>
            <p className="text-slate-600 text-sm mb-2 line-clamp-3">{testimonial.text_en}</p>
            <p className="text-slate-500 text-sm italic line-clamp-2">{testimonial.text_zh}</p>
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <Switch checked={testimonial.active} onCheckedChange={() => handleToggleActive(testimonial)} />
                <span className="text-xs text-slate-500">{testimonial.active ? 'Visible' : 'Hidden'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(testimonial)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(testimonial.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Testimonial Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Customer Name</label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Rating (1-5)</label>
                <Input type="number" min="1" max="5" value={formData.rating} onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Location (English)</label>
                <Input value={formData.location_en} onChange={(e) => setFormData({ ...formData, location_en: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Location (Chinese)</label>
                <Input value={formData.location_zh} onChange={(e) => setFormData({ ...formData, location_zh: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Review Text (English)</label>
              <Textarea rows={3} value={formData.text_en} onChange={(e) => setFormData({ ...formData, text_en: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Review Text (Chinese)</label>
              <Textarea rows={3} value={formData.text_zh} onChange={(e) => setFormData({ ...formData, text_zh: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.active} onCheckedChange={(checked) => setFormData({ ...formData, active: checked })} />
              <span className="text-sm">Active (visible on website)</span>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-slate-900">Save Testimonial</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTestimonials;
